import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../../features/auth/slice';
import { Upload, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import UserAvatar from '../../components/UserAvatar';

import { Editor } from '@tinymce/tinymce-react';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/code';
import 'tinymce/plugins/table';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';

function WritePage({ boardType = 'Notice' }) {
  const navigate = useNavigate();
  const { me, distributorTheme } = useSelector(authSelector.selectAuthState);
  const isDark = distributorTheme === 'dark';
  const [nickName, setNickName] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [title, setTitle] = useState('');
  const [isNotice, setIsNotice] = useState(false);
  const [secretYn, setSecretYn] = useState(false);
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const writer = me?.name;
  const clientId = me?.id;

  useEffect(() => {
    if (!clientId) return;
    axios.get(`/client/${clientId}`)
      .then((res) => setNickName(res.data.nickName))
      .catch(console.error);
  }, [clientId]);

  useEffect(() => {
    axios
      .get('/board/category/load', {
        params: { boardType },
        withCredentials: true,
      })
      .then((response) => {
        setCategories(response.data);
        if (response.data.length > 0) {
          setSelectedCategory(response.data[0].id.toString());
        }
      });
  }, [boardType]);

  const handleSubmit = () => {
    if (!title.trim()) {
      alert('제목을 입력해 주세요.');
      return;
    }
    const formData = new FormData();
    if (file) formData.append('file', file);
    formData.append('writer', writer);
    formData.append('title', title);
    formData.append('description', content);
    formData.append('categoryId', selectedCategory);
    formData.append('isNotice', isNotice ? 1 : 0);
    formData.append('clientId', clientId);
    formData.append('secretYn', secretYn ? 'Y' : 'N');

    setLoading(true);
    axios
      .post(`/board?boardType=${boardType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      })
      .then((response) => {
        navigate(
          `/${boardType}/View/${response.data.newArticle.id}?boardType=${boardType}`
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <div className="rounded-2xl border border-white/[0.08] bg-[#182334] overflow-hidden">
        <div className="px-6 py-4 bg-white/[0.03] border-b border-white/[0.08] text-slate-200 text-base font-semibold">
          글쓰기
        </div>

        <div className="p-6 space-y-5">
          {/* 작성자 */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-20 shrink-0">
              작성자
            </label>
            <div className="flex items-center gap-2">
              <UserAvatar
                name={nickName || writer}
                profileImage={me?.profileImage}
                userType={me?.userType}
                adminYn={me?.adminYn}
                size={24}
              />
              <span className="text-sm text-slate-200">{nickName || writer}</span>
            </div>
          </div>

          {/* 카테고리 */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              카테고리
            </label>
            <select
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200 transition"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}>
              {categories.map((category) => (
                <option key={category.id} value={category.id.toString()}>
                  {category.category}
                </option>
              ))}
            </select>
          </div>

          {/* 제목 */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">제목</label>
            <input
              className="w-full py-2 px-3 rounded-xl border border-white/[0.08] text-sm outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent bg-[#1a2b42] text-slate-200 transition"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="제목을 입력하세요"
            />
          </div>

          {/* 옵션 체크박스 */}
          <div className="flex items-center gap-5">
            {me?.adminYn === 'Y' && (
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                  isNotice ? 'bg-sky-500 border-sky-500' : 'border-white/20 bg-white/5 group-hover:border-white/30'
                }`}
                  onClick={() => setIsNotice(!isNotice)}>
                  {isNotice && <span className="text-white text-[10px] font-bold">✓</span>}
                </div>
                <span className="text-sm text-slate-300">공지글</span>
              </label>
            )}
            <label className="flex items-center gap-2 cursor-pointer group">
              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                secretYn ? 'bg-sky-500 border-sky-500' : 'border-white/20 bg-white/5 group-hover:border-white/30'
              }`}
                onClick={() => setSecretYn(!secretYn)}>
                {secretYn && <span className="text-white text-[10px] font-bold">✓</span>}
              </div>
              <span className="text-sm text-slate-300">비밀글</span>
            </label>
          </div>

          {/* 내용 */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">내용</label>
            <Editor
              value={content}
              init={{
                height: 350,
                menubar: false,
                base_url: '/tinymce',
                skin: isDark ? 'oxide-dark' : 'oxide',
                content_css: isDark ? 'dark' : 'default',
                toolbar:
                  'undo redo | formatselect fontsize | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | table | link | code',
                plugins: ['code', 'table', 'lists', 'link', 'autolink'],
                fontsize_formats: '8pt 10pt 12pt 14pt 18pt 24pt 36pt',
                content_style:
                  'body { font-family:Helvetica,Arial,sans-serif; font-size:12pt !important; color: #1f2937; }',
              }}
              onEditorChange={(newValue) => setContent(newValue)}
            />
          </div>

          {/* 파일 첨부 */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              파일 첨부
            </label>
            <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer transition-colors">
              <Upload size={18} className="text-slate-500" />
              <span className="text-sm text-slate-400">
                {file ? file.name : '파일을 선택하세요'}
              </span>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-5">
        <button
          onClick={() => navigate(`/${boardType}/List`)}
          className="flex items-center gap-1.5 px-4 py-2 text-slate-400 hover:text-white text-sm font-medium transition-colors">
          <ArrowLeft size={16} />
          목록
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`px-5 py-2 rounded-xl text-white text-sm font-semibold transition-colors ${
            loading
              ? 'bg-slate-600 cursor-not-allowed'
              : 'bg-sky-500 hover:bg-sky-600'
          }`}>
          {loading ? '등록 중...' : '등록'}
        </button>
      </div>
    </>
  );
}

export default WritePage;
